import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

/**
 * Format an ISO-UTC timestamp in Saudi Arabia local time (Asia/Riyadh, UTC+3, no DST).
 * We use `formatToParts` so the output stays Latin digits + a compact `YYYY-MM-DD HH:mm`
 * shape which reads well in an admin table regardless of the browser's locale.
 */
function saudiTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Riyadh",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
  } catch { return "—"; }
}

export default function AdminPurchases() {
  const [purchases, setPurchases] = useState([]);
  const [q, setQ] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("paid");   // default: only paid
  const [plan, setPlan] = useState("any");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [busy, setBusy] = useState(null);  // purchase_id currently being resent
  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (paymentStatus && paymentStatus !== "any") params.payment_status = paymentStatus;
    if (plan && plan !== "any") params.plan = plan;
    if (includeDeleted) params.include_deleted = "true";
    return api.get("/admin/purchases", { params }).then((r) => setPurchases(r.data));
  };
  useEffect(() => { load(); }, [paymentStatus, plan, includeDeleted]);

  const resend = async (p) => {
    if (!window.confirm(`إعادة إرسال البريد + الواتساب إلى ${p.buyer_email}؟\nسيتم إنشاء رابط تفعيل جديد وإبطال الروابط السابقة.`)) return;
    setBusy(p.id);
    try {
      await api.post(`/admin/purchases/${p.id}/resend-welcome`);
      toast.success(`تم إرسال بريد الترحيب + الواتساب إلى ${p.buyer_email}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الإرسال");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-purchases">
      <h1 className="text-3xl font-display font-semibold">المشتريات</h1>
      <p className="text-sm text-foreground/60">
        استخدم زر <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs"><Send className="h-3 w-3" /> إعادة إرسال</span>{" "}
        لأي عميل لم يستلم بريد التفعيل أو رسالة الواتساب — سنُنشئ رابط تفعيل جديد ونرسله فورًا.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap gap-2 items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالبريد أو رقم المعاملة..." className="bg-white border-foreground/10 text-foreground max-w-xs" data-testid="purchases-search" />
        <select
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          className="border rounded px-2 py-1.5 text-xs bg-white h-9"
          data-testid="purchases-payment-status-filter"
        >
          <option value="paid">مدفوع فقط</option>
          <option value="pending">بانتظار الدفع</option>
          <option value="refunded">مسترد</option>
          <option value="any">الكل</option>
        </select>
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="border rounded px-2 py-1.5 text-xs bg-white h-9"
          data-testid="purchases-plan-filter"
        >
          <option value="any">جميع الخطط</option>
          <option value="subscription">اشتراك</option>
          <option value="onetime">دفعة واحدة (قديم)</option>
          <option value="installment">أقساط (قديم)</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
            data-testid="purchases-include-deleted"
          />
          إظهار المحذوفين
        </label>
        <Button type="submit">بحث</Button>
        <span className="text-xs text-foreground/60 ms-auto" data-testid="purchases-count">
          {purchases.length} نتيجة
        </span>
      </form>
      <Card className="bg-white border-foreground/10">
        <Table>
          <TableHeader>
            <TableRow className="border-foreground/10 hover:bg-transparent">
              <TableHead className="text-foreground">البريد</TableHead>
              <TableHead className="text-foreground">رقم الجوال</TableHead>
              <TableHead className="text-foreground">المنتج</TableHead>
              <TableHead className="text-foreground">المصدر</TableHead>
              <TableHead className="text-foreground">المبلغ</TableHead>
              <TableHead className="text-foreground">حالة الدفع</TableHead>
              <TableHead className="text-foreground">حالة الوصول</TableHead>
              <TableHead className="text-foreground whitespace-nowrap">وقت الاشتراك (السعودية)</TableHead>
              <TableHead className="text-foreground">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchases.map((p) => {
              const canResend = ["paid", "partially_paid"].includes(p.payment_status);
              return (
                <TableRow key={p.id} className="border-foreground/10" data-testid={`purchase-row-${p.id}`}>
                  <TableCell className="text-xs">{p.buyer_email}</TableCell>
                  <TableCell className="text-xs font-mono whitespace-nowrap" dir="ltr">
                    {p.phone ? (
                      <a href={`https://wa.me/${p.phone.replace(/[^\d]/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {p.phone}
                      </a>
                    ) : <span className="text-foreground/40">—</span>}
                  </TableCell>
                  <TableCell>{p.product_name || "—"}</TableCell>
                  <TableCell><Badge variant="outline">{p.source}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">
                    <div>{p.amount} {p.currency}</div>
                    {p.currency && p.currency !== "SAR" && p.amount_sar != null && (
                      <div className="text-[10px] text-foreground/50">≈ {Math.round(p.amount_sar)} SAR</div>
                    )}
                  </TableCell>
                  <TableCell><Badge variant={p.payment_status === "paid" ? "default" : "secondary"}>{p.payment_status === "paid" ? "مدفوع" : p.payment_status === "refunded" ? "مسترد" : p.payment_status}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{p.access_status === "granted" ? "ممنوح" : "بانتظار دعوة"}</Badge></TableCell>
                  <TableCell className="text-xs font-mono whitespace-nowrap" dir="ltr" title={p.created_at}>
                    {saudiTime(p.created_at)}
                  </TableCell>
                  <TableCell>
                    {canResend && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1"
                        onClick={() => resend(p)}
                        disabled={busy === p.id}
                        data-testid={`resend-welcome-${p.id}`}
                        title="إعادة إرسال بريد التفعيل + واتساب"
                      >
                        {busy === p.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <><Send className="h-3 w-3" /> إعادة إرسال</>}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {purchases.length === 0 && <TableRow><TableCell colSpan={9} className="text-center opacity-60 py-8">لا مشتريات</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
